import { Inject, Injectable } from '@angular/core';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS, CONSTANTS_VALUE } from '../../config/constants.module';
import { ColDef, SetFilterValuesFuncParams } from 'ag-grid-community';
import { LanguageService } from '../../shared/service/language.service';
import { GraphicListColumn } from '../models/ag-grid-list-column.model';
import { AttributeConfig, CodedValueDomain } from '../models/attribute-config.model';
import { AttributeType } from '../../shared/enum/attribute-type.enum';
import { ConvertUtil } from '../../shared/util/convert-util';
import { CellClassParams } from '../models/cell-class-params.model';

@Injectable({
  providedIn: 'root',
})
export class AgGridColumnsTransformService {
  private blanks = {
    displayKey: 'blanks',
    displayName: 'Empty',
    test: function (filterValue, cellValue) {
      return cellValue == null;
    },
    hideFilterInput: true,
  };

  private noBlanks = {
    displayKey: 'noBlanks',
    displayName: 'Not empty',
    test: function (filterValue, cellValue) {
      return cellValue != null;
    },
    hideFilterInput: true,
  };

  constructor(
    private readonly decimalPipe: DecimalPipe,
    private readonly currencyPipe: CurrencyPipe,
    /* private readonly chfPipe: ChfPipe,*/
    private readonly translate: TranslateService,
    private readonly languageService: LanguageService,
    @Inject(CONSTANTS) private readonly constants: any
  ) {}

  buildColumnFromDef(attributeConfig: AttributeConfig): GraphicListColumn {
    let fieldPrefix = attributeConfig.internal ? attributeConfig.path : 'feature';
    fieldPrefix = fieldPrefix ? `${fieldPrefix}.` : '';
    return {
      field: `${fieldPrefix}${attributeConfig.prop}`,
      header: attributeConfig.name,
      domain: attributeConfig.domain,
      type: attributeConfig.type,
      valueFormatter: attributeConfig.valueFormatter,
      showCompleteDomain: attributeConfig.showCompleteDomain,
      stringCodedDomain: attributeConfig.stringCodedDomain,
      width: attributeConfig.width,
      sortable: attributeConfig.sortable !== undefined ? attributeConfig.sortable : true,
      hidden: attributeConfig.hidden
    };
  }

  transformToColumnDefs(
    columns: GraphicListColumn[],
    floatingFilters: boolean,
    getDomainFunc: (params: SetFilterValuesFuncParams) => void
  ): ColDef[] {
    const columnDefs: ColDef[] = [];

    columns.forEach(column => {
      // set column specific values only
      const columnDef: ColDef = {
        headerName: column.header ? this.translate.instant(column.header) : '',
        headerTooltip: column.header ? this.translate.instant(column.header) : '',
        field: this.friendlyFieldPathName(column.field),
        floatingFilter: floatingFilters,
        floatingFilterComponentParams: {
          debounceMs: CONSTANTS_VALUE.agGrid.columnDef.debounceTime,
        },
        filterParams: {
          buttons: ['reset'],
          debounceMs: CONSTANTS_VALUE.agGrid.columnDef.debounceTime,
          refreshValuesOnOpen: true,
        },
        type: this.getColumnType(column),
        hide: column.hidden,
        width: column.width,
        sortable: column.sortable,
        suppressSizeToFit: !!column.width,
      };

      const cssClasses = [];
      const headerCssClasses = [];

      if (column.cssClass) {
        cssClasses.push(column.cssClass);
      }

      if (column.headerCssClass) {
        headerCssClasses.push(column.headerCssClass);
      }

      if (column.isEditable) {
        columnDef.editable = column.isEditable;
        cssClasses.push('editable-cell');
      }

      if (column.dividerColumn) {
        cssClasses.push('column-divider');
      }

      if (column.flexValue) {
        columnDef.flex = column.flexValue;
        columnDef.width = undefined;
      }

      if (column.minWidth) {
        columnDef.minWidth = column.minWidth;
      }

      if (column.maxWidth) {
        columnDef.maxWidth = column.maxWidth;
      }

      if (column.hidden) {
        columnDef.hide = column.hidden;
      }

      if (column.headerComponent) {
        columnDef.headerComponent = column.headerComponent;
        columnDef.headerComponentParams = column.headerParams;
      }

      if (column.isValidFunc) {
        columnDef.cellClassRules = {
          'invalid-cell': function (params: CellClassParams) {
            return !column.isValidFunc(params);
          },
        };
      }

      if (column.checkboxSelection) {
        columnDef.checkboxSelection = true;
      }

      switch (column.type) {
        case AttributeType.number:
          columnDef.filter = 'agNumberColumnFilter';
          columnDef.filterParams.filterOptions = [
            'equals',
            'empty',
            'lessThan',
            'greaterThan',
            this.blanks,
            this.noBlanks,
          ];
          if (column.valueFormatter) {
            const formattedValue = params => {
              return this.formatValue(column, params.value, params.data);
            };
            columnDef.valueFormatter = formattedValue;
            columnDef.tooltipValueGetter = formattedValue;
          }
          cssClasses.push('ag-right-aligned-cell');

          if (column.isEditable) {
            columnDef.cellEditor = 'numericEditor';
          }
          break;
        case AttributeType.decimalPercent:
          columnDef.filter = 'agNumberColumnFilter';
          columnDef.filterParams.filterOptions = [
            'equals',
            'empty',
            'lessThan',
            'greaterThan',
            this.blanks,
            this.noBlanks,
          ];
          const formattedDecimalPercent = params => {
            return this.mapToDecimalPercent(params.value);
          };
          columnDef.valueFormatter = formattedDecimalPercent;
          columnDef.tooltipValueGetter = formattedDecimalPercent;
          cssClasses.push('ag-right-aligned-cell');
          break;
        case AttributeType.intPercent:
          columnDef.filter = 'agNumberColumnFilter';
          columnDef.filterParams.filterOptions = [
            'equals',
            'empty',
            'lessThan',
            'greaterThan',
            this.blanks,
            this.noBlanks,
          ];
          const formattedIntPercent = params => {
            return this.mapToIntPercent(params.value);
          };
          columnDef.valueFormatter = formattedIntPercent;
          columnDef.tooltipValueGetter = formattedIntPercent;
          cssClasses.push('ag-right-aligned-cell');
          break;
        case AttributeType.domain:
          columnDef.filter = 'agSetColumnFilter';
          columnDef.filterParams.suppressMiniFilter = true;
          columnDef.filterParams.buttons = ['reset', 'apply'];

          // Set Filter values predefined from all possible domains given by esri service
          if (column.showCompleteDomain) {
            columnDef.filterParams.values = params => {
              params.success(column.domain.map(o => o.code));
            };
          } else {
            if (getDomainFunc === null) {
              throw new Error(
                'AgGridColumnsTransformService: getDomainFunc is required to show partial domain in filters'
              );
            }
            columnDef.filterParams.values = getDomainFunc;
          }

          columnDef.filterParams.cellRenderer = params => {
            if (params.value === CONSTANTS_VALUE.agGrid.emptyFilterValue) {
              return this.translate.instant('COMMON.empty');
            }
            const text = this.mapDomain(params.value, column.domain, column.stringCodedDomain);
            return `<span title="${text}">${text}</span>`;
          };

          // filter list sorting
          columnDef.filterParams.comparator = (a, b) => {
            return this.domainComparator(a, b, column.domain, column.stringCodedDomain);
          };

          // column value sorting
          columnDef.comparator = (a, b) => {
            return this.domainComparator(a, b, column.domain, column.stringCodedDomain);
          };

          // domain values shown in cells as translated strings
          const formattedDomain = params => {
            return this.mapDomain(params.value, column.domain, column.stringCodedDomain);
          };
          columnDef.valueFormatter = formattedDomain;
          columnDef.tooltipValueGetter = formattedDomain;
          break;
        case AttributeType.boolean:
          columnDef.filter = 'agSetColumnFilter';
          columnDef.filterParams.suppressMiniFilter = true;
          columnDef.filterParams.values = params => {
            params.success(column.domain.map(o => o.code));
          };
          columnDef.filterParams.cellRenderer = params => {
            return this.mapDomain(params.value, column.domain, false);
          };
          const formattedBoolean = params => {
            return this.mapDomain(params.value, column.domain, false);
          };

          // filter list sorting
          columnDef.filterParams.comparator = (a, b) => {
            return this.domainComparator(a, b, column.domain, false);
          };

          // column value sorting
          columnDef.comparator = (a, b) => {
            return this.domainComparator(a, b, column.domain, false);
          };

          columnDef.valueFormatter = formattedBoolean;
          columnDef.tooltipValueGetter = formattedBoolean;
          break;
        case AttributeType.hasValue:
          columnDef.filter = 'agSetColumnFilter';
          // domain values shown in cells as translated strings
          const formattedHasValue = params => {
            return this.mapHasValue(params.value, column.domain);
          };
          columnDef.valueFormatter = formattedHasValue;
          columnDef.tooltipValueGetter = formattedHasValue;
          columnDef.keyCreator = formattedHasValue;
          break;
        case AttributeType.date:
          columnDef.filter = 'agDateColumnFilter';
          const formattedDate = params => {
            return params.value
              ? new Date(params.value).toLocaleDateString(this.languageService.selectedLanguage.lang)
              : '';
          };
          columnDef.cellRenderer = formattedDate;
          columnDef.tooltipValueGetter = formattedDate;
          columnDef.minWidth = column.flexValue ? null : 205;
          break;
        case AttributeType.id:
          columnDef.filter = 'agNumberColumnFilter';
          columnDef.filterParams.filterOptions = ['equals', 'lessThan', 'greaterThan'];

          break;
        case AttributeType.action:
          columnDef.cellRenderer = 'actionCellRenderer';
          // columnDef.cellRendererParams = column.actionParams;
          columnDef.width = 40;
          columnDef.minWidth = 40;
          columnDef.sortable = false;
          columnDef.type = null;
          cssClasses.push('action-cell');
          columnDef.suppressSizeToFit = true;
          break;
        case AttributeType.checkbox:
          columnDef.width = 100;
          columnDef.minWidth = 90;
          columnDef.suppressSizeToFit = true;
          columnDef.type = null;
          columnDef.cellRenderer = 'checkboxCellRenderer';
          // columnDef.cellRendererParams = column.checkboxParams;
          columnDef.headerComponent = 'checkboxHeader';
          columnDef.headerComponentParams = column.headerParams;
          columnDef.sortable = false;
          break;
        case AttributeType.customLabel:
          columnDef.suppressSizeToFit = true;
          if (column.valueFormatter) {
            const formattedValue = params => {
              return this.formatValue(column, params.value, params.data);
            };
            columnDef.valueFormatter = formattedValue;
            columnDef.tooltipValueGetter = formattedValue;
          }
          break;
        default:
          columnDef.filter = 'agTextColumnFilter';
          columnDef.filterParams.filterOptions = ['contains', 'empty', 'equals', this.blanks, this.noBlanks];
          columnDef.tooltipField = this.friendlyFieldPathName(column.field);
          break;
      }

      if (cssClasses.length > 0) {
        columnDef.cellClass = cssClasses;
      }

      if (headerCssClasses.length > 0) {
        columnDef.headerClass = headerCssClasses;
      }

      columnDefs.push(columnDef);
    });

    return columnDefs;
  }

  mapDomain(value: any, domain: CodedValueDomain[], stringCodedDomain: boolean): string {
    // HACK: param.value is converted to string by keyCreator... -> pass in number if
    // domain not coded as strin
    if (value === CONSTANTS_VALUE.agGrid.selectAll) {
      return this.translate.instant('COMMON.selectAll');
    }

    const typedValue = value && !stringCodedDomain ? Number(value) : value;
    return ConvertUtil.transform(typedValue, domain);
  }

  mapHasValue(value: any, domain: CodedValueDomain[]): string {
    return ConvertUtil.transformHasValue(value, domain);
  }

  mapToDecimalPercent(value: number): string {
    return ConvertUtil.transformToDecimalPercent(value);
  }

  mapToIntPercent(value: number): string {
    return ConvertUtil.transformToIntPercent(value);
  }

  friendlyFieldPathName(field) {
    return field.replace('feature.attributes.', '');
  }

  private getColumnType(col: GraphicListColumn): string {
    if ([AttributeType.number, AttributeType.decimalPercent, AttributeType.intPercent].includes(col.type)) {
      return 'rightAligned';
    }
    return null;
  }

  private formatValue(col: GraphicListColumn, value: any, rowData: any): any {
    if ((!value || value === '') && value !== 0) return;

    let formattedValue: any = null;

    let unit = '';

    if (col.valueFormatter.keysFromDomain) {
      formattedValue = ConvertUtil.transformKeys(value, col.valueFormatter.keysFromDomain);
    }

    if (col.valueFormatter.unitFromColum) {
      unit = rowData[col.valueFormatter.unitFromColum];
      if (col.valueFormatter.unitFromColumDomain) {
        unit = ConvertUtil.transform(unit, col.valueFormatter.unitFromColumDomain);
      }
    }

    // round float to fix after decimal point
    if (col.valueFormatter.decimal) {
      formattedValue = this.decimalPipe.transform(value, col.valueFormatter.decimal);
    }

    // round number into integer
    if (col.valueFormatter.isInteger) {
      formattedValue = Math.round(value);
    }

    if (col.valueFormatter.isCurrency) {
      /*formattedValue = this.chfPipe.transform(value);
      if (col.valueFormatter.unitFromColum) {
        formattedValue = `${formattedValue}/${unit}`;
      }*/
    } else if (unit) {
      formattedValue = `${formattedValue} ${unit}`;
    }

    if (col.valueFormatter.symbol) {
      formattedValue = formattedValue + ' ' + col.valueFormatter.symbol;
    }

    return formattedValue;
  }

  private domainComparator(a: any, b: any, domain: CodedValueDomain[], stringCodedDomain: boolean): number {
    // Force empty value at first position
    if (a === CONSTANTS_VALUE.agGrid.emptyFilterValue) return -1;
    if (b === CONSTANTS_VALUE.agGrid.emptyFilterValue) return 1;

    // Map coded values to string and compare
    const valA = this.mapDomain(a, domain, stringCodedDomain);
    const valB = this.mapDomain(b, domain, stringCodedDomain);

    return valA.toLowerCase().localeCompare(valB.toLowerCase());
  }
}
